import React from 'react'
import { MobileView } from 'react-device-detect'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  $DCprimaryColor,
  DcBreadcrumbs,
  DcDateRangePicker,
  DcDiv,
  DcFlexBox,
  DcText,
  PixelPageContainer,
  Section,
  SectionContent,
  colors,
} from 'pixel-base'
import { useUrlFilters } from 'pixel-base'
import CloseIcon from 'utils/icons/close-icon'

import ComplaintsChart from './components/ComplaintsChart'
import OrdersAgingChart from './components/OrderAgingChart'
import OrdersChart from './components/OrdersChart'
import RevenueChart from './components/RevenueChart'
import OrdersComparisonChart from './components/ShippingPerformance'
import ChartDetail from './components/subComponents/ChartDetail'
import TopCitiesChart from './components/TopCitiesChart'
import TopCitiesDetailPane from './components/TopCitiesDetailPane'
import TopCollectionsChart from './components/TopCollectionsChart'
import TopProductsChart from './components/TopProductsChart'
import TopProductsDetailPane from './components/TopProductsDetailPane'
import { useDetailCitiesData } from './components/useDetailCitiesData'
import { useDetailProductsData } from './components/useDetailProductsData'
import DashboardMobileFilters from './DashboardMobileFilters'
import themes from './themes.json'

const breadcrumbs = [
  {
    name: 'Analytics',
    active: false,
  },
  {
    name: 'Dashboard',
    active: true,
  },
]

const CHART_COMPONENTS = {
  ordersChart: OrdersChart,
  revenueChart: RevenueChart,
  topProductsChart: TopProductsChart,
  topCollectionsChart: TopCollectionsChart,
  topCitiesChart: TopCitiesChart,
  ordersComparisonChart: OrdersComparisonChart,
  ordersAgingChart: OrdersAgingChart,
  complaintsChart: ComplaintsChart,
} as const

const CHART_TITLES = {
  ordersChart: 'Orders Chart Details',
  revenueChart: 'Revenue Chart Details',
  topProductsChart: 'Top Products Chart Details',
  topCollectionsChart: 'Top Collections Chart Details',
  topCitiesChart: 'Top Cities Chart Details',
  ordersComparisonChart: 'Orders Comparison Chart Details',
  ordersAgingChart: 'Orders Aging Chart Details',
  complaintsChart: 'Complaints Chart Details',
} as const

interface ChartFilters {
  start_date: Date
  end_date: Date
  data_type: string
}

const Dashboard = () => {
  const { filters, updateFilters } = useUrlFilters()
  const [date, setDate] = React.useState<ChartFilters>(filters)

  const [activeTheme, setActiveTheme] = React.useState('themeA')
  const [showDetail, setShowDetail] = React.useState<boolean>(false)
  const [component, setComponent] = React.useState<string>('')

  // Separate filters for detail pane
  const [detailFilters, setDetailFilters] = React.useState<ChartFilters>({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    end_date: new Date(),
    data_type: 'daily',
  })

  // Hook to fetch detailed products data (limit 100)
  const detailProductsData = useDetailProductsData(
    detailFilters,
    showDetail && component === 'topProductsChart',
  )

  // Hook to fetch detailed cities data
  const detailCitiesData = useDetailCitiesData(
    detailFilters,
    showDetail && component === 'topCitiesChart',
  )

  const handleOnChange = React.useCallback(
    (startDate: Date, endDate: Date, dataType: string) => {
      const newFilters = {
        start_date: startDate,
        end_date: endDate,
        data_type: dataType || 'daily',
      }
      setDate(newFilters)
      updateFilters(newFilters)
    },
    [updateFilters],
  )

  // Handler for detail pane filters (only updates detail chart)
  const handleDetailFiltersChange = React.useCallback(
    (newFilters: ChartFilters) => {
      setDetailFilters(newFilters)
    },
    [],
  )

  // Memoize the chart component to prevent unnecessary re-renders
  const memoizedChartComponent = React.useMemo(() => {
    if (!component || !CHART_COMPONENTS[component]) {
      return null
    }

    const ChartComponent = CHART_COMPONENTS[component]
    return (
      <ChartComponent
        filters={filters}
        setComponent={setComponent}
        setShowDetail={setShowDetail}
        width="100%"
      />
    )
  }, [component, filters, setComponent, setShowDetail])

  return (
    <>
      <PixelPageContainer maxHeight="100vh" height="100vh" padding="0px">
        <DcFlexBox
          height="60px"
          style={{
            fontSize: '16px',
            padding: '8px 0px',
            borderBottom: `1px solid ${colors.grayBorderLight}`,
            backgroundColor: colors.white,
          }}
        >
          <DcFlexBox
            justifyContent="space-between"
            alignItems="center"
            padding=".5rem 20px"
          >
            <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
            <ProfileSection />
          </DcFlexBox>
        </DcFlexBox>
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
            <DcFlexBox
              height="auto"
              padding="5px 20px"
              justifyContent="space-between"
            >
              <DcFlexBox
                height="auto"
                gap="5px"
                style={{ width: '70%' }}
                justifyContent="flex-start"
              ></DcFlexBox>
              <DcFlexBox height="auto" justifyContent="flex-end" width="30%">
                <DcDateRangePicker
                  size="sm"
                  month={2}
                  ranges={{
                    start_date: date.start_date,
                    end_date: date.end_date,
                  }}
                  onApply={() => {
                    const newFilters = {
                      start_date: date.start_date,
                      end_date: date.end_date,
                      data_type: date.data_type || 'daily',
                    }
                    updateFilters(newFilters)
                  }}
                  onChange={(e) => {
                    handleOnChange(
                      e.range1.startDate,
                      e.range1.endDate,
                      e.range1.data_type || 'daily',
                    )
                  }}
                />
              </DcFlexBox>
            </DcFlexBox>
            {
              <MobileView>
                <DashboardMobileFilters
                  filters={filters}
                  themes={themes}
                  activeTheme={activeTheme}
                  setActiveTheme={setActiveTheme}
                  setFilters={updateFilters}
                  handleSubmit={() => {
                    // No-op for now as per the commented code above
                  }}
                />
              </MobileView>
            }
          </SectionContent>
        </Section>

        <DcFlexBox style={{ height: 'calc(100vh - 100px)' }}>
          <Section
            style={{
              display: 'flex',
              padding: '20px',
              height: 'calc(100vh - 100px)',
              flexDirection: 'column',
              overflowY: 'scroll',
              gap: '20px',
            }}
          >
            <Section
              style={{
                zIndex: 10,
                display: 'flex',
                gap: '20px',
                padding: '0px',
              }}
            >
              <DcDiv style={{ width: '50%' }}>
                <OrdersChart
                  filters={filters}
                  setComponent={setComponent}
                  setShowDetail={setShowDetail}
                />
              </DcDiv>
              <DcDiv style={{ width: '50%' }}>
                <RevenueChart
                  filters={filters}
                  setComponent={setComponent}
                  setShowDetail={setShowDetail}
                />
              </DcDiv>
            </Section>
            <Section
              style={{
                zIndex: 10,
                display: 'flex',
                gap: '20px',
                padding: '0px',
              }}
            >
              <DcDiv style={{ width: '50%' }}>
                <TopProductsChart
                  filters={filters}
                  setComponent={setComponent}
                  setShowDetail={setShowDetail}
                  onDetailClick={() => {
                    setComponent('topProductsChart')
                    setShowDetail(true)
                  }}
                />
              </DcDiv>
              <DcDiv style={{ width: '50%' }}>
                <TopCitiesChart
                  filters={filters}
                  setComponent={setComponent}
                  setShowDetail={setShowDetail}
                />
              </DcDiv>
            </Section>
            <DcFlexBox height="auto">
              <DcFlexBox gap="20px" height="auto">
                <DcDiv style={{ width: '50%' }}>
                  <TopCollectionsChart
                    filters={filters}
                    setComponent={setComponent}
                    setShowDetail={setShowDetail}
                  />
                </DcDiv>
                <DcDiv style={{ width: '50%' }}>
                  <OrdersComparisonChart
                    filters={filters}
                    setComponent={setComponent}
                    setShowDetail={setShowDetail}
                  />
                </DcDiv>
              </DcFlexBox>
            </DcFlexBox>
            <DcFlexBox height="auto">
              <DcFlexBox gap="20px" height="auto">
                <DcDiv style={{ width: '50%' }}>
                  <OrdersAgingChart
                    filters={filters}
                    setComponent={setComponent}
                    setShowDetail={setShowDetail}
                  />
                </DcDiv>
                <DcDiv style={{ width: '50%' }}>
                  <ComplaintsChart
                    filters={filters}
                    setComponent={setComponent}
                    setShowDetail={setShowDetail}
                  />
                </DcDiv>
              </DcFlexBox>
            </DcFlexBox>
          </Section>
        </DcFlexBox>
      </PixelPageContainer>
      <div
        style={{
          zIndex: 100,
          width: '100%',
          height: '100%',
          right: showDetail ? '0' : '-100%',
          backgroundColor: 'rgba(46, 49, 74, 0.2)',
          opacity: 1,
          position: 'absolute',
          cursor: 'pointer',
        }}
        onClick={() => {
          setShowDetail(false)
        }}
      />

      <DcFlexBox
        style={{
          zIndex: 100,
          width: '100%',
          height: '100%',
          right: showDetail ? '0' : '-100%',
          top: '0',
          border: `1px solid ${colors.grayBorderLight}`,
          backgroundColor: colors.white,
          padding: '10px',
          position: 'absolute',
          flexDirection: 'column',
          transition: 'right 0.5s ease-in-out',
        }}
      >
        <DcFlexBox
          height="30px"
          width="100%"
          backgroundColor={colors.white}
          padding="0 10px 10px 10px"
          justifyContent="space-between"
        >
          <DcFlexBox
            justifyContent="space-between"
            height="auto"
            padding="0 10px 5px 0"
          >
            <DcText style={{ fontWeight: '700' }}>
              {CHART_TITLES[component as keyof typeof CHART_TITLES] || null}
            </DcText>
          </DcFlexBox>
          <DcDiv
            hoverStyle={{
              color: $DCprimaryColor,
              cursor: 'pointer',
            }}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setShowDetail(false)
            }}
            color="rgba(0, 0, 0, 0.5)"
            width="15px"
          >
            <CloseIcon size={16} />
          </DcDiv>
        </DcFlexBox>
        <DcDiv height="calc(100vh - 30px)">
          {component === 'topProductsChart' ? (
            <TopProductsDetailPane
              data={detailProductsData.products}
              loading={detailProductsData.loading}
              onClose={() => setShowDetail(false)}
              onFiltersChange={handleDetailFiltersChange}
              dashboardFilters={filters}
            />
          ) : component === 'topCitiesChart' ? (
            <TopCitiesDetailPane
              data={detailCitiesData.cities}
              loading={detailCitiesData.loading}
              filters={detailFilters}
              onClose={() => setShowDetail(false)}
              onFiltersChange={handleDetailFiltersChange}
            />
          ) : (
            <ChartDetail component={memoizedChartComponent} />
          )}
        </DcDiv>
      </DcFlexBox>
    </>
  )
}

export default Dashboard
